import { PageRequest } from '@/core/request/PageRequest';
import { Manufacturer } from '../../operational/Manufacturers/Manufacturers.types';
import { SubArea } from '../../operational/SubAreas/SubAreas.types';
import { SubGroup } from '../../operational/SubGroups/SubGroups.types';
import {
  CostItem,
  PartItemFieldOption,
} from '../Configuration/Configuration.types';

export enum StockItemType {
  Part = 1,
  Consumable = 2,
}

export const StockItemTypeOptions = {
  [StockItemType.Part]: 'domain.enums.item_type.part',
  [StockItemType.Consumable]: 'domain.enums.item_type.consumable',
};

export enum FilterTypeEnum {
  free = 0,
  specific = 1,
}

export enum StockOrderColumn {
  barcode = 0,
  partId = 1,
  consumableId = 2,
  originalCode = 8,
  manufacturingCode = 4,
  manufacturer = 5,
  group = 6,
  description = 7,
  companyCode = 9,
}

export const StockOrderColumnConfig: Record<string, number> = {
  [PartItemFieldOption.BarCode]: StockOrderColumn.barcode,
  [PartItemFieldOption.OriginalCode]: StockOrderColumn.originalCode,
  [PartItemFieldOption.ManufacturingCode]: StockOrderColumn.manufacturingCode,
  [PartItemFieldOption.Manufacturer]: StockOrderColumn.manufacturer,
  [PartItemFieldOption.Group]: StockOrderColumn.group,
  [PartItemFieldOption.Description]: StockOrderColumn.description,
  [PartItemFieldOption.companyCode]: StockOrderColumn.companyCode,
};

export interface StockItemPrice {
  company: {
    id: number;
    name: string;
  };
  cost: number;
  price: number;
  location?: string;
  realCost: number;
}
export interface StockItem {
  observation?: string;
  partId: number;
  consumableId: number;
  barcode: string;
  originalCode: string;
  imageUrl?: string;
  sku: string;
  manufacturingCode: string;
  companyCode: number;
  subGroup: SubGroup;
  subArea: SubArea;
  manufacturer: Manufacturer;
  description: string;
  prices: StockItemPrice[];
  isPartReplacement: boolean;
  stock: {
    company: {
      id: number;
      name: string;
    };
    stock: number;
    reserved: number;
    availableStock: number;
  }[];
  totalStock: number;
  applications: StockApplications[];
}

export interface ListStockItemsQuery extends PageRequest {
  ids?: number[];
  active?: boolean;
  companiesIds?: number[] | string[];
  itemType?: StockItemType;
  below?: boolean;
  barcode?: string;
  originalCode?: string;
  manufacturingCode?: string;
  manufacturer?: number;
  subGroup?: number;
  description?: string;
  observation?: string;
  search?: string;
  orderColumns?: StockOrderColumn;
  vehicleId?: number;
  partReplacement?: number;
  companyCode?: string;
  sku?: string;
  filterType?: FilterTypeEnum;
  brandId?: number;
  modelId?: number;
  location?: string;
  isPartReplacement?: boolean;
  availableStock?: boolean;
  partApplicationDescription?: string;
  prioritizePrefixMatch?: boolean;
}

export interface PartStockPricesResult {
  company: {
    id: number;
    name: string;
  };
  stock: number;
  availableStock: number;
  reservedStock: number;
  minStock: number;
  maxStock: number;
  location: string;
  buyPrice: number;
  ipi: number;
  freightCost: number;
  taxReplacement: number;
  fixedCost: number;
  costFields: CostItem[];
  otherExpenses: number;
  maxDiscount: number;
  icmsExemption: number;
  sellPrice: number;
  realCost: number;
  profitMargin: number;
  commissionPercentage: number;
}
export interface PartStockPrices {
  result: PartStockPricesResult[];
}

export interface CompanyPartStockPricesRequest {
  partId: number;
  companyId: number;
  minStock: number;
  maxStock: number;
  buyPrice: number;
  ipi: number;
  taxReplacement: number;
  freightCost: number;
  otherExpenses: number;
  location?: string;
  icmsExemption: number;
  sellPrice: number;
  maxDiscount: number;
  commissionPercentage: number;
}

export interface ConsumableStockPricesResult {
  company: {
    id: number;
    name: string;
  };
  stock: number;
  avaiableStock: number;
  reservedStock: number;
  minStock: number;
  maxStock: number;
  location: string;
  buyPrice: number;
  ipi: number;
  freightCost: number;
  taxReplacement: number;
  fixedCost: number;
  costFields: CostItem[];
  otherExpenses: number;
  operationalExpenses: number;
  enabled: boolean;
  realCost: number;
}

export interface ConsumableStockPrices {
  result: ConsumableStockPricesResult[];
}

export interface CompanyConsumableStockPricesRequest {
  consumableId: number;
  companyId: number;
  minStock: number;
  maxStock: number;
  buyPrice: number;
  ipi: number;
  taxReplacement: number;
  freightCost: number;
  otherExpenses: number;
  location?: string;
  operationalExpenses: number;
}

export enum AffectPriceSetPartPriceMethod {
  byManufacturer,
  byGroup,
  allStock,
}

export enum SetPartPriceOption {
  Adjust = 0,
  Change = 1,
}

export enum AffectPriceType {
  byCostPrice,
  bySalePrice,
  byProfitPercentage,
}
export enum AffectPriceAffect {
  AffectSalesPrice,
  AffectPercentageProfitMargin,
}

export interface AffectPriceRequest {
  companyIds: number[];
  groupIds?: number[];
  manufacturerIds?: number[];
  setPartPriceMethod: AffectPriceSetPartPriceMethod;
  type: AffectPriceType;
  affect?: AffectPriceAffect;
  option?: SetPartPriceOption;
  percentageIncidence: number;
}

export interface AffectPriceFormValues {
  companyIds: number[];
  groupIds?: number[];
  manufacturerIds?: number[];
  setPartPriceMethod: AffectPriceSetPartPriceMethod;
  type: AffectPriceType;
  affect?: AffectPriceAffect;
  option?: SetPartPriceOption;
  percentageIncidence: number;
}

export interface StockApplications {
  description: string;
  fromYear: number;
  toYear: number;
}
